<!-- FORM BLOCK COMPONENT
    SUMMARY:
        Takes a FormBlockItem (described below) property, and recursively builds a form from this specified input.
        Bound FormBlockItem allows for live form building in-browser, or pre-specified form building.
    DEPENDENCIES:
        See components array for list of component dependencies.
    FormBlockItem Specification:
        Required Properties:
            BlockType: string.  Must be either: "Container or InputElement". (Containers hold other FormBlocks and InputElements do not)
        Required Properties for BlockType = Container:
            ChildBlocks: array.  List of other FormBlock objects.
            ContainerType: string. Type of container used.  Available Values are:
                ElementContainer: Creates FormBlock for all items in ChildBlocks
                RowWithChildPerColumn: Creates a row element, and places each item in ChildBlocks into a col-md element. Equal Column widths.
                Fieldset: Creates a fieldset element with legend, and write FormBlockElements inside.
        Required Properties for BlockType = InputElement:
            ElementType: string.  Type of input element to write.  Available values are:
                H1
                H2
                H3
                H4
                StringInput
                DateInput
                TextAreaInput
                TextAreaAutosizeInput
                SelectInput
                RadioInput
-->

<template>
    <div class="FormBlock" v-bind:class="FormBlockItem.Active ? 'active' : ''">
        <!--HANDLE CONTAINER OBJECTS-->
        <div v-if="FormBlockItem.BlockType == 'Container'">
            <div v-if="FormBlockItem.ContainerType == 'ElementContainer'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div>
                        Child Elements: {{ FormBlockItem.ChildBlocks.length }}
                    </div>
                    <div class="col-md-11 offset-md-1">
                        <FormBlock v-for="formBlockChild in FormBlockItem.ChildBlocks" :chart-id="ChartId" v-bind:form-block-item="formBlockChild" v-bind:input-values="InputValues" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                    </div>
                </div>
                <div v-else>
                    <FormBlock v-for="formBlockChild in FormBlockItem.ChildBlocks" :chart-id="ChartId" v-bind:form-block-item="formBlockChild" v-bind:input-values="InputValues" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                </div>
            </div>
            <div v-else-if="FormBlockItem.ContainerType == 'RowWithChildPerColumn'" class="row">
                <div v-if="Editor" style="width:100%;">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div>
                        Child Elements: {{ FormBlockItem.ChildBlocks.length }}
                    </div>
                    <div class="col-md-11 offset-md-1">
                        <FormBlock v-for="formBlockChild in FormBlockItem.ChildBlocks" :chart-id="ChartId" v-bind:form-block-item="formBlockChild" v-bind:input-values="InputValues" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                    </div>
                </div>
                <div v-else v-for="formBlockChild in FormBlockItem.ChildBlocks" class="col-md">
                    <FormBlock v-bind:form-block-item="formBlockChild" v-bind:input-values="InputValues" :chart-id="ChartId" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                </div>
            </div>
            <div v-else-if="FormBlockItem.ContainerType == 'Fieldset'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline" style="background-color:yellow;">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div>
                        Child Elements: {{ FormBlockItem.ChildBlocks.length }}
                    </div>
                    <div v-if="!DisplayBlockItemCondition" class="red">
                        ITEM IN PREVIEW CONDITIONALLY HIDDEN
                    </div>
                    <div class="form-group">
                        <label>Legend:</label>
                        <input v-model="FormBlockItem.Legend" class="form-control" />
                    </div>
                    <div class="col-md-11 offset-md-1">
                        <FormBlock v-for="formBlockChild in FormBlockItem.ChildBlocks" :chart-id="ChartId" v-bind:form-block-item="formBlockChild" v-bind:input-values="InputValues" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                    </div>

                </div>
                <fieldset v-else-if="DisplayBlockItemCondition">
                    <legend>{{ FormBlockItem.Legend }}</legend>
                    <FormBlock v-for="formBlockChild in FormBlockItem.ChildBlocks" :chart-id="ChartId" v-bind:form-block-item="formBlockChild" :chart-id="ChartId" v-bind:input-values="InputValues" :editor="Editor" :show-reviewer-comments="ShowReviewerComments" :edit-reviewer-comments="EditReviewerComments" />
                </fieldset>
            </div>
        </div>
        
        <!--HANDLE ELEMENT OBJECTS-->
        <div v-else-if="FormBlockItem.BlockType == 'InputElement'">
            <!--HEADINGS-->
            <div v-if="FormBlockItem.ElementType == 'H1'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Value:</label>
                        <input v-model="FormBlockItem.Value" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                </div>
                <div v-else>
                    <h1 v-bind:class="FormBlockItem.Class">{{ FormBlockItem.Value }}</h1>
                </div>
            </div>
            <div v-else-if="FormBlockItem.ElementType == 'H2'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Value:</label>
                        <input v-model="FormBlockItem.Value" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                </div>
                <div v-else>
                    <h2 v-bind:class="FormBlockItem.Class">{{ FormBlockItem.Value }}</h2>
                </div>
            </div>
            <div v-else-if="FormBlockItem.ElementType == 'H3'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Value:</label>
                        <input v-model="FormBlockItem.Value" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                </div>
                <div v-else>
                    <h3 v-bind:class="FormBlockItem.Class">{{ FormBlockItem.Value }}</h3>
                </div>
            </div>
            <div v-else-if="FormBlockItem.ElementType == 'H4'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Value:</label>
                        <input v-model="FormBlockItem.Value" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                </div>
                <div v-else>
                    <h4 v-bind:class="FormBlockItem.Class">{{ FormBlockItem.Value }}</h4>
                </div>
            </div>
            <div v-else-if="FormBlockItem.ElementType == 'P'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Value:</label>
                        <input v-model="FormBlockItem.Value" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                </div>
                <div v-else>
                    <p v-bind:class="FormBlockItem.Class">{{ FormBlockItem.Value }}</p>
                </div>
            </div>
            <!--STRING INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'StringInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Read Only:</label>
                        <select v-model="FormBlockItem.ReadOnly" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else>
                    <div class="form-group">
                        <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                        <input type="text" :name="FormBlockItem.Name" v-bind:readonly="FormBlockItem.ReadOnly" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]" :placeholder="FormBlockItem.Placeholder" />
                    </div>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>

            </div>

            <!--INTEGER INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'IntegerInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Read Only:</label>
                        <select v-model="FormBlockItem.ReadOnly" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Tooltip:</label>
                        <textarea class="form-control" v-model="FormBlockItem.Tooltip"></textarea>
                    </div>
                </div>
                <div v-else>
                    <div class="form-group">
                        <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}<span v-if="FormBlockItem.Tooltip" v-b-tooltip.hover :title="FormBlockItem.Tooltip"><font-awesome-icon class="info-icon" :icon="['fas', 'info-circle']" /></span></label>
                        <input type="number" step=".01" :name="FormBlockItem.Name" v-bind:readonly="FormBlockItem.ReadOnly" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]" :placeholder="FormBlockItem.Placeholder" />
                    </div>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>

            </div>

            

            <!--STRING INPUT NO LABEL-->
            <div v-else-if="FormBlockItem.ElementType == 'StringInputNoLabel'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else>
                    <input type="text" :name="FormBlockItem.Name" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]" :placeholder="FormBlockItem.Placeholder" style="margin-top:-15px;" />
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>

            </div>

            <!--DATE INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'DateInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Read Only:</label>
                        <select v-model="FormBlockItem.ReadOnly" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else-if="FormBlockItem.ReadOnly" class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <input type="text" :name="FormBlockItem.Name" v-bind:readonly="true" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]" :placeholder="FormBlockItem.Placeholder" />
                </div>
                <div v-else class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <flat-pickr :name="FormBlockItem.Name" v-bind:class="FormBlockItem.Class" class="form-control" v-bind:required="FormBlockItem.Required" :config="DateInputConfigSchema(FormBlockItem)" v-model="InputValuesData[FormBlockItem.Name]"></flat-pickr>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                </textarea>
                </div>
            </div>

            <!--TIME INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'TimeInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <flat-pickr :name="FormBlockItem.Name" v-bind:class="FormBlockItem.Class" class="form-control" v-bind:required="FormBlockItem.Required" :config="TimeInputConfigSchema(FormBlockItem)" v-model="InputValuesData[FormBlockItem.Name]"></flat-pickr>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>

            <!--TEXTAREA INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'TextAreaInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Rows:</label>
                        <input v-model="FormBlockItem.Rows" type="number" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else-if="DisplayBlockItemCondition" class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <textarea :name="FormBlockItem.Name" v-bind:rows="FormBlockItem.Rows" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]">
                    </textarea>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>



            <!--TEXTAREA-AUTOSIZE INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'TextAreaAutosizeInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Base Height:</label>
                        <input v-model="FormBlockItem.BaseHeight" type="number" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Max Height:</label>
                        <input v-model="FormBlockItem.MaxHeight" type="number" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Base Content:</label>
                        <input v-model="FormBlockItem.BaseContent" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <div v-else-if="DisplayBlockItemCondition" class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <TextAreaExpandable :name="FormBlockItem.Name"
                                        :base-height="FormBlockItem.BaseHeight"
                                        :max-height="FormBlockItem.MaxHeight"
                                        v-bind:class="FormBlockItem.Class"
                                        v-bind:required="FormBlockItem.Required"
                                        v-bind:placeholder="FormBlockItem.Placeholder"
                                        v-model="InputValuesData[FormBlockItem.Name]" />
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>

            <!--SELECT INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'SelectInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Options: <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteSelectOption" class="pointer red" /> <font-awesome-icon :icon="['fa', 'plus']" v-on:click="AddSelectOption" class="pointer olive-green-text" /></label>
                        <div v-for="(selectOption, index) in FormBlockItem.SelectOptions">
                            <div class="underline">{{index + 1}}. Option Settings:</div>
                            <label>Value:</label>
                            <input v-model="selectOption.value" type="text" class="form-control" />
                            <label>Text:</label>
                            <input v-model="selectOption.text" type="text" class="form-control" />
                            <label>Hidden:</label>
                            <select v-model="selectOption.hidden" class="form-control">
                                <option :value="false">False</option>
                                <option :value="true">True</option>
                            </select>
                            <label>Selected:</label>
                            <select v-model="selectOption.selected" class="form-control">
                                <option :value="false">False</option>
                                <option :value="true">True</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div v-else class="form-group">
                    <label v-if="FormBlockItem.Label != null">{{ FormBlockItem.Label }}</label>
                    <select :name="FormBlockItem.Name" @change="OnSelectChange" v-bind:required="FormBlockItem.Required" class="form-control" v-bind:class="FormBlockItem.Class" v-model="InputValuesData[FormBlockItem.Name]">
                        <option v-for="selectOption in FormBlockItem.SelectOptions" v-bind:selected="selectOption.selected" v-bind:value="selectOption.value" v-bind:hidden="selectOption.hidden">
                            {{ selectOption.text }}
                        </option>
                    </select>
                </div>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--RADIO INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'RadioInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Stacked:</label>
                        <select v-model="FormBlockItem.Stacked" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Options: <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteRadioOption" class="pointer red" /> <font-awesome-icon :icon="['fa', 'plus']" v-on:click="AddRadioOption" class="pointer olive-green-text" /></label>
                        <div v-for="(selectOption, index) in FormBlockItem.RadioOptions">
                            <div class="underline">{{index + 1}}. Option Settings:</div>
                            <label>Value:</label>
                            <input v-model="selectOption.value" type="text" class="form-control" />
                            <label>Text:</label>
                            <input v-model="selectOption.text" type="text" class="form-control" />
                            <label>Selected:</label>
                            <select v-model="selectOption.selected" class="form-control">
                                <option :value="false">False</option>
                                <option :value="true">True</option>
                            </select>
                        </div>
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-radio-group plain
                                        v-bind:required="FormBlockItem.Required"
                                        v-bind:stacked="FormBlockItem.Stacked"
                                        :options="FormBlockItem.RadioOptions"
                                        :name="FormBlockItem.Name"
                                        v-model="InputValuesData[FormBlockItem.Name]"></b-form-radio-group>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--CHECKBOX INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'CheckInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="CheckChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Stacked:</label>
                        <select v-model="FormBlockItem.Stacked" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Options: <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteCheckOption" class="pointer red" /> <font-awesome-icon :icon="['fa', 'plus']" v-on:click="AddCheckOption" class="pointer olive-green-text" /></label>
                        <div v-for="(selectOption, index) in FormBlockItem.CheckOptions">
                            <div class="underline">{{index + 1}}. Option Settings:</div>
                            <label>Value:</label>
                            <input v-model="selectOption.value" type="text" class="form-control" />
                            <label>Text:</label>
                            <input v-model="selectOption.text" type="text" class="form-control" />
                            <label>Selected:</label>
                            <select v-model="selectOption.selected" class="form-control">
                                <option :value="false">False</option>
                                <option :value="true">True</option>
                            </select>
                        </div>
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-checkbox-group plain
                                           v-bind:required="FormBlockItem.Required"
                                           v-bind:stacked="FormBlockItem.Stacked"
                                           :options="FormBlockItem.CheckOptions"
                                           :name="FormBlockItem.Name"
                                           v-model="InputValuesData[FormBlockItem.Name]"></b-form-checkbox-group>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--CHECKBOX FROM API INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'CheckInputFromAPI'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="CheckChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Stacked:</label>
                        <select v-model="FormBlockItem.Stacked" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Endpoint:</label>
                        <input v-model="FormBlockItem.Endpoint" class="form-control" />
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-checkbox-group plain
                                           v-bind:required="FormBlockItem.Required"
                                           v-bind:stacked="FormBlockItem.Stacked"
                                           :options="FormBlockItem.CheckOptions"
                                           :name="FormBlockItem.Name"
                                           v-model="InputValuesData[FormBlockItem.Name]"></b-form-checkbox-group>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--IMAGE INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'ImageInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-file accept="image/jpeg, image/png"
                                 v-bind:class="FormBlockItem.Class"
                                 :name="FormBlockItem.Name"
                                 v-bind:required="FormBlockItem.Required"
                                 :placeholder="FormBlockItem.Placeholder"
                                 drop-placeholder="Drop image here..."></b-form-file>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--DOCUMENT INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'DocumentInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-file accept=".pdf, .docx, .doc, .xlsx"
                                 v-bind:class="FormBlockItem.Class"
                                 :name="FormBlockItem.Name"
                                 v-bind:required="FormBlockItem.Required"
                                 :placeholder="FormBlockItem.Placeholder"
                                 drop-placeholder="Drop word or pdf here..."></b-form-file>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
            <!--SCAN FILE INPUT-->
            <div v-else-if="FormBlockItem.ElementType == 'ScanFileInput'">
                <div v-if="Editor">
                    {{ FormBlockItem.Index + 1 }}. <input type="radio" ref="activeselector" name="activeselector" @change="RadioChange" /><span class="bold underline">{{ FormBlockItem.Title }}</span> <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="DeleteThisFormBlockItem" class="pointer red" /> <font-awesome-icon :icon="['fas', 'arrow-up']" v-on:click="MoveUp" class="pointer" /> <font-awesome-icon :icon="['fas', 'arrow-down']" v-on:click="MoveDown" class="pointer" />
                    <div class="form-group">
                        <label>Label:</label>
                        <input v-model="FormBlockItem.Label" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Name:</label>
                        <input v-model="FormBlockItem.Name" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Class:</label>
                        <input v-model="FormBlockItem.Class" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Placeholder:</label>
                        <input v-model="FormBlockItem.Placeholder" class="form-control" />
                    </div>
                    <div class="form-group">
                        <label>Required:</label>
                        <select v-model="FormBlockItem.Required" class="form-control">
                            <option :value="false">False</option>
                            <option :value="true">True</option>
                        </select>
                    </div>
                </div>
                <b-form-group v-else :label="FormBlockItem.Label">
                    <b-form-file accept=".pdf, .jpg, .jpeg, .png"
                                 v-bind:class="FormBlockItem.Class"
                                 :name="FormBlockItem.Name"
                                 v-bind:required="FormBlockItem.Required"
                                 :placeholder="FormBlockItem.Placeholder"
                                 drop-placeholder="Drop scanned document here..."></b-form-file>
                </b-form-group>
                <div v-if="ShowReviewerComments" class="form-group">
                    <label>Review's Comments:</label>
                    <textarea :readonly="!EditReviewerComments" :name="'ReviewerComment_' + FormBlockItem.Name" v-bind:rows="4" class="form-control" style="background-color:aqua" v-model="InputValuesData['ReviewerComment_' + FormBlockItem.Name]">
                    </textarea>
                </div>
            </div>
        </div>
        
    </div>

</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    //import { faEdit } from '@fortawesome/free-solid-svg-icons';
    //library.add(faEdit);
    import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
    library.add(faInfoCircle);

    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';

    import TextAreaExpandable from '../CustomInputElements/TextAreaExpandable.vue';

    import FormBlock from "./FormBlock.vue";

    import { EventBus } from "../../../services/event-bus.js";

    export default {
        created: function () {
            let vm = this;
            EventBus.$on('form-block-radio-change', msg => {
                if (vm.$refs['activeselector'] != null) {
                    if (vm.$refs['activeselector'].checked) {
                        vm.FormBlockItem.Active = true;

                    } else {
                        vm.FormBlockItem.Active = false;
                    }
                }
            });
        },
        beforeDestroy() {
            EventBus.$off('form-block-radio-change');
        },
        name: "FormBlock",
        props: [
            "FormBlockItem",
            "InputValues",
            "Editor",
            "ShowReviewerComments",
            "EditReviewerComments",
            "ChartId",
        ],
        data: function () {
            return {
                InputValuesData: {},
            }
        },
        methods: {
            DateInputConfigSchema(formBlockItem) {
                let vm = this;
                return {
                    dateFormat: 'Y-m-d',
                    allowInput: true,
                    altInput: true,
                    altFormat: 'm/d/Y',
                    placeholder: formBlockItem.Placeholder,
                    disableMobile: "true",
                    onChange: function (selectedDates, dateStr, instance) {
                        //console.log("ON CHANGE DATEINPUT SCHEMA:")
                        //console.log(selectedDates);
                        //console.log(dateStr);
                        if (dateStr === '') { instance.close(); }
                        if (formBlockItem.Name == "DateOfService") {
                            EventBus.$emit('duplicate-check-needed');
                        }

                        //HANDLE AUTH START DATE ENTRIES
                        if (formBlockItem.Name == 'AuthFrom') {
                            //Set Field AuthTo to 6 months forward NTE end of June
                            let startDate = vm.InputValuesData['AuthFrom'];
                            let startDateObj = moment(startDate, 'YYYY-MM-DD');
                            let endDateObj = null;
                            //if the start month is more than 6, set end date as June 30.  Otherwise, add 6 months to start date
                            /*console.log("The start date month is " + startDateObj.month())*/
                            if (startDateObj.month() <= 5) {
                                endDateObj = moment(startDateObj.year() + "-06-30", 'YYYY-MM-DD');
                            } else {
                                endDateObj = startDateObj.add(6, 'M');
                            }

                            //Set value of AuthTo
                            vm.$set(vm.InputValuesData, 'AuthTo', endDateObj.format('YYYY-MM-DD'));
                            vm.$forceUpdate();
                        }
                        //HANDLE FIRST CLINICAL SYNCHRONIZATION FOR ELMR FORMS
                        //to future devs on this project, yes we're being hacky :)
                        let firstClinicalFieldsNames = ['Date_csi', 'Dte_mad', 'DteDia_dia', 'OpenDate_dia', 'PreadmitDte_mad', 'TdyDte_dia', 'Dte_asup', 'EffDte1_asup', 'EffDte2_asup', 'EffDte3_asup']
                        if (firstClinicalFieldsNames.includes(formBlockItem.Name)) {
                            firstClinicalFieldsNames.forEach(function (item) {
                                vm.$set(vm.InputValuesData, item, dateStr);
                            });
                            vm.$forceUpdate();
                        }
                    }
                };
            },
            TimeInputConfigSchema(formBlockItem) {
                let vm = this;
                return {
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i',
                    allowInput: true,
                    altInput: true,
                    altFormat: 'h:i K',
                    placeholder: formBlockItem.Placeholder,
                    onChange: function (selectedDates, dateStr, instance) {
                        if (dateStr === '') { instance.close(); }
                        if ((formBlockItem.Name == "StartTime" || formBlockItem.Name == "EndTime")) {
                            //Attempt to update TotalServiceTime
                            if (vm.InputValuesData.hasOwnProperty('TotalServiceTime')) {
                                //console.log("going to calculate time now....");
                                let startTime = vm.InputValuesData['StartTime'];
                                let endTime = vm.InputValuesData['EndTime'];
                                if (formBlockItem.Name == "StartTime") {
                                    startTime = dateStr;
                                } else {
                                    endTime = dateStr;
                                }
                                if (startTime && endTime) {
                                    if (startTime == "00:00:00") {
                                        startTime = "00:00";
                                    }
                                    if (endTime == "00:00:00") {
                                        endTime = "00:00";
                                    }
                                    //console.log("StartTime:");
                                    //console.log(startTime);
                                    //console.log("EndTime:");
                                    //console.log(endTime);
                                    //moment(this.StartDate, 'YYYY-MM-DD').add(magnitude, 'days').format("YYYY-MM-DD");
                                    let startTimeObj = moment((new moment()).format("YYYY-MM-DD") + " " + startTime, "YYYY-MM-DD HH:mm");
                                    let endTimeObj = moment((new moment()).format("YYYY-MM-DD") + " " + endTime, "YYYY-MM-DD HH:mm");
                                    //correct time if next day
                                    if (endTimeObj < startTimeObj) {
                                        //console.log(startTime);
                                        //console.log(endTime);
                                        endTimeObj = endTimeObj.add(1, 'days');
                                    }
                                    let durationObj = moment.duration(endTimeObj.diff(startTimeObj));
                                    let durationMin = durationObj.asMinutes();
                                    //console.log("total minutes:");
                                    //console.log(durationMin);
                                    //vm.InputValuesData['TotalServiceTime'] = durationMin;
                                    vm.$set(vm.InputValuesData, 'TotalServiceTime', durationMin);
                                    vm.$forceUpdate();
                                    //vm.InputValuesData['TotalServiceTime'] = durationMin;
                                    let code = vm.ComputeMHServiceCode(durationMin, vm.InputValuesData["ServiceProvided"])
                                    vm.$set(vm.InputValuesData, 'ServiceCode', code);
                                    /*console.log("performing forced update from on change time event");*/
                                    vm.$forceUpdate();
                                }
                            } else {
                                /*console.log("no input prop of TotalServiceTime");*/
                            }
                        } else {
                            /*console.log("not a start or end time");*/
                        }
                    }
                };
            },
            formatDate(value, fmt = 'D MMM YYYY') {
                return (value == null)
                    ? ''
                    : moment(value, 'YYYY-MM-DD').format(fmt)
            },
            GetInputValue(valueName) {
                if (this.InputValues.hasOwnProperty(valueName)) {
                    return this.InputValues[valueName];
                } else {
                    return "";
                }
            },
            RadioChange(a) {
                EventBus.$emit('form-block-radio-change', this.FormBlockItem);
            },
            CheckChange(a) {
                EventBus.$emit('form-block-check-change', this.FormBlockItem);
            },
            CheckForDuplicateServiceExistanceError(error) {
                //set selected service back to null
                //this.InputValuesData['ServiceProvided'] = null;
            },
            OnSelectChange(a) {
                let selection = a.target.value;
                console.log("a.target.value");
                console.log(a.target.value);
                //respond if name = ServiceProvided
                if (this.FormBlockItem.Name == 'ServiceProvided') {

                    //this.InputValuesData['ServiceCode'] = (new moment()).format("YYYY-MM-DD HH:mm");
                    if (this.InputValuesData['TotalServiceTime']) {
                        let code = this.ComputeMHServiceCode(this.InputValuesData['TotalServiceTime'], selection)
                        this.$set(this.InputValuesData, 'ServiceCode', code);
                        EventBus.$emit('duplicate-check-needed');
                    }
                }
                //respond if name = MHNoteTemplate
                if (this.FormBlockItem.Name == 'MHNoteTemplate') {
                    console.log("setting note template")
                    let text = null;
                    switch (selection) {
                        case "CalAims":
                            text = "Activity/Intervention:\n\n\nConsumer Progress or Perspective: \n\n\nPlan: \n\n\nNotes: \n\n\nNext Appointment:\n\n";
                            break;
                        case "Care Plan Progress Note":
                            text = "Activity/Intervention: \n\n\nDue to mental health challenges, consumer needs assistance with:\n\n\nActivities to help meet the need(s): \n\n\nPerson(s) who will assist: \n\n\nQuote from consumer what they will do to partner in meeting these needs: \n\n";
                            break;
                        case "Psychiatrist":
                            text = "Subjective:\n\n\nCurrent Medications:\n\n\nLabs:\n\n\nAIMS:\n\n\nNote Addresses Which Treatment Plan Problem:\n\n\nNotes:\n\n\n";
                            break;
                        case "SOAP":
                            text = "Subjective:\n\nObjective:\n\nAssessment:\n\nPlan:\n";
                            break;
                        default:
                            text = null;
                    }
                    if (text != null) {
                        this.$set(this.InputValuesData, 'ProgNoteNarrative', text);
                        this.$forceUpdate();
                    }

                }
            },
            //COMPUTE MH SERVICE CODE
            ComputeMHServiceCode(totalServiceTime, serviceProvided) {
                if (totalServiceTime <= 0) {
                    return "300NB";
                }
                let code = "";
                switch (serviceProvided) {
                    case "AssessmentIndividual":
                        code = "90791A";
                        break;
                    case "CarePlanProgressNoteClientAndCaregiver":
                        code = "590ICC";
                        break;
                    case "CarePlanProgressNoteClientOnly":
                        code = "520ICC";
                        break;
                    case "CrisisIntervention":
                        code = "90839CA";
                        break;
                    case "FamilyTherapyWithClient":
                        code = "90847A";
                        break;
                    case "GroupRehab":
                        code = "363";
                        break;
                    case "GroupTherapy":
                        code = "90853A";
                        break;
                    case "IndividualTherapy":
                        if (totalServiceTime >= 53) {
                            code = "90837A";
                        } else if (totalServiceTime >= 38) {
                            code = "90834CA";
                        } else if (totalServiceTime >= 16) {
                            code = "90832CA"
                        } else {
                            code = "300NB"
                        }
                        break;
                    case "IntensiveCareCoordination":
                        code = "520ICC";
                        break;
                    case "IntensiveCareCoordinationReview":
                        code = "530ICCR";
                        break;
                    case "IntensiveCareCoordinationWithFamily":
                        code = "590ICC";
                        break;
                    case "IntensiveHomeBasedServices":
                        code = "360IHBS"
                        break;
                    case "MedicationServicesF2F":
                        //////////////////////
                        break;
                    case "MedicationServicesNonF2F":
                        code = "99215NF"
                        break;
                    case "NoteToChart":
                        code = "300NB"
                        break;
                    case "RehabService":
                        code = "360";
                        break;
                    case "CaseManagementWithFamily":
                        code = "590";
                        break;
                    case "CaseManagementWithNonFamily":
                        code = "520";
                        break;
                    case "TreatmentPlanReview":
                        code = "520ICC";
                        break;
                    case "AssessmentPathwaysToWellness":
                        code = "90791KTA";
                        break;
                    case "PeerICCEngagement":
                        code = "622ICC";
                        break;
                    case "PeerMHEngagement":
                        code = "622";
                        break;
                    case "PeerICCTherapeuticActivity":
                        code = "621ICC";
                        break;
                    case "PeerIHBSTherapeuticActivity":
                        code = "621IHBS";
                        break;
                    case "PeerMHTherapeuticActivity":
                        code = "621";
                        break;
                    case "PeerICCR":
                        code = "621ICCR";
                        break;
                    default:
                        code = null;
                        break;
                }
                return code;
            },
            DeleteThisFormBlockItem() {
                this.$parent.DeleteChildFormBlockItem(this.FormBlockItem.Index);
            },
            DeleteChildFormBlockItem(index) {
                if (this.FormBlockItem.ChildBlocks != null && this.FormBlockItem.ChildBlocks[index] != null) {
                    this.FormBlockItem.ChildBlocks.splice(index, 1);
                    //refactor index numbers
                    for (var i = 0; i < this.FormBlockItem.ChildBlocks.length; i++) {
                        this.FormBlockItem.ChildBlocks[i].Index = i;
                    }
                }
            },
            MoveUp() {
                this.$parent.ReplaceChildBlock(this.FormBlockItem.Index, this.FormBlockItem.Index - 1);
            },
            MoveDown() {
                this.$parent.ReplaceChildBlock(this.FormBlockItem.Index, this.FormBlockItem.Index + 1);
            },
            ReplaceChildBlock(oldIndex, newIndex) {
                //check to make sure the move is possible
                if (this.FormBlockItem.ChildBlocks != null && this.FormBlockItem.ChildBlocks[oldIndex] != null && this.FormBlockItem.ChildBlocks[newIndex] != null) {
                    //move item
                    let temp = this.FormBlockItem.ChildBlocks[oldIndex];
                    this.$set(this.FormBlockItem.ChildBlocks, oldIndex, this.FormBlockItem.ChildBlocks[newIndex]);
                    //this.FormBlockItem.ChildBlocks[oldIndex] = this.FormBlockItem.ChildBlocks[newIndex];
                    this.$set(this.FormBlockItem.ChildBlocks, newIndex, temp);
                    //this.FormBlockItem.ChildBlocks[newIndex] = temp;
                    //refactor index numbers
                    for (var i = 0; i < this.FormBlockItem.ChildBlocks.length; i++) {
                        this.FormBlockItem.ChildBlocks[i].Index = i;
                    }
                }
            },
            AddSelectOption() {
                this.FormBlockItem.SelectOptions.push({
                    text: "",
                    value: "",
                    selected: false,
                    hidden: false
                });
            },
            DeleteSelectOption() {
                this.FormBlockItem.SelectOptions.pop();
            },
            AddRadioOption() {
                this.FormBlockItem.RadioOptions.push({
                    text: "",
                    value: "",
                    selected: false
                });
            },
            DeleteRadioOption() {
                this.FormBlockItem.RadioOptions.pop();
            },
            AddCheckOption() {
                this.FormBlockItem.CheckOptions.push({
                    text: "",
                    value: "",
                    selected: false
                });
            },
            DeleteCheckOption() {
                this.FormBlockItem.CheckOptions.pop();
            },
            FetchOptions() {
                this.$MasterAPI.ExecuteRequest("GET", this.FormBlockItem.Endpoint, { ChartID: this.ChartId }, this.FetchOptionsSuccess, () => { }, true);
            },
            FetchOptionsSuccess(response) {
                this.FormBlockItem.CheckOptions = response.data.data;
            }
        },
        components: {
            FormBlock,
            flatPickr,
            TextAreaExpandable
        },
        computed: {
            DisplayBlockItemCondition: function () {
                if (this.FormBlockItem.ContainerType == 'Fieldset') {
                    //GROUP INFORMATION (display only if 'ServiceProvided' == 'GroupRehab')
                    if (this.FormBlockItem.Legend == "GROUP INFORMATION") {
                        if (this.InputValuesData['ServiceProvided'] == "GroupRehab") {
                            return true;
                        } else {
                            return false;
                        }
                    }
                    //MENTAL HEALTH STATUS EXAM (display only if 'ProviderBillingRole' == 'Clinician (LPHA)')
                    if (this.FormBlockItem.Legend == "MENTAL HEALTH STATUS EXAM") {
                        if (this.InputValuesData['ProviderBillingRole'] == "Clinician (LPHA)") {
                            return true;
                        } else {
                            return false;
                        }
                    }
                    //SIGNIFICANT EVENTS (display only if 'ProviderBillingRole' == 'Rehab Specialist')
                    if (this.FormBlockItem.Legend == "SIGNIFICANT EVENTS") {
                        if (this.InputValuesData['ProviderBillingRole'] == "Rehab Specialist") {
                            return true;
                        } else {
                            return false;
                        }
                    }
                    //MENTAL HEALTH ClIENT ASSESSMENT (display School Development section depending on child age 0-5 or 6+)
                    if (this.FormBlockItem.Legend == "SCHOOL/DEVELOPMENT HISTORY (FOR AGES 0-5)") {
                        if (this.InputValuesData['Age'] <= 5) {
                            return true;
                        } else {
                            return false;
                        }
                    }
                    if (this.FormBlockItem.Legend == "SCHOOL/DEVELOPMENT HISTORY (FOR AGES 6+)") {
                        if (this.InputValuesData['Age'] >= 6) {
                            return true;
                        } else {
                            return false;
                        }
                    }

                }
                if (this.FormBlockItem.ElementType == 'TextAreaAutosizeInput' || this.FormBlockItem.ElementType == 'TextAreaInput') {
                    //Focus of Treatment
                    if (this.FormBlockItem.Label == "Focus of Treatment (Appears on monthly summaries only):") {
                        if (this.InputValuesData['ProviderBillingRole'] == "Clinician (LPHA)" || this.InputValuesData['ProviderBillingRole'] == "Rehab Specialist") {
                            return true;
                        } else {
                            return false;
                        }
                    }
                    //PNW ILS Caregiver Support Plan
                    if (this.FormBlockItem.Class && this.FormBlockItem.Class.includes("conditional_display_no_initial") && this.InputValuesData['ReportType'] == 'Initial') {
                        return false;
                    } else {
                        return true;
                    }
                }
                return true;
            },

        },
        mounted: function () {
            this.$set(this, 'InputValuesData', this.InputValues);
            //Add default time to values if TotalServiceTime name exists in formblock and if it doesn't already exist
            if (this.FormBlockItem.Name == "TotalServiceTime" && this.InputValuesData.hasOwnProperty("TotalServiceTime") == false) {
                //this.InputValuesData['TotalServiceTime'] = 0;
                this.$set(this.InputValuesData, "TotalServiceTime", 0);
            }
            if (this.FormBlockItem.ElementType == "CheckInputFromAPI") {
                this.FetchOptions();
            }
        },
        beforeDestroy: function () {
            this.$set(this, 'InputValuesData', {});
        }
    }
</script>

<style scoped>

    .FormBlock {
        width: 100%;
    }
    textarea {
        resize: none;
    }

    .FormBlock .active {
        border-style: solid;
        border-width: 1px;
        border-color: red;
    }
</style>